import { useEffect, useState, useCallback, useMemo } from "react";
import { Route, Routes } from "react-router-dom";
import {
  replaceCDNUrl,
  detectMediaType,
  parseBooleanString,
  getAllQueryParams,
} from "./utils/helpers";
import { ROUTES, CDN_OLD_URL, CDN_NEW_URL } from "./utils/constants";

// Auto generates routes from files under ./pages
// https://vitejs.dev/guide/features.html#glob-import
const pages = import.meta.glob("./pages/*.jsx", { eager: true });

const routes = Object.keys(pages).map((path) => {
  const name = path.match(/\.\/pages\/(.*)\.jsx$/)[1];
  return {
    name,
    path: name === "Home" ? "/" : `/${name.toLowerCase()}`,
    component: pages[path].default,
  };
});

export function App() {
  const [backgroundUrl, setBackgroundUrl] = useState("");
  const [mounted, setMounted] = useState(false);
  const [popup, setPopup] = useState(false);
  const [windowContext, setWindowContext] = useState(null);
  const [isImage, setIsImage] = useState(false);
  const [isVideo, setIsVideo] = useState(false);

  const DynamicComponent = useMemo(() => routes[0]?.component, []);

  /**
   * Parse window context from preview mode or global context
   */
  const parseWindowContext = useCallback(() => {
    try {
      let context = null;

      if (window.location.pathname === ROUTES.PREVIEW) {
        const params = getAllQueryParams();
        if (params.questions) {
          params.questions = JSON.parse(params.questions);
        }
        context = { ...params, is_preview: true };
      } else {
        context = window?.context || null;
      }

      return context;
    } catch (error) {
      console.error("Error parsing window context:", error);
      return null;
    }
  }, []);

  /**
   * Process context data: replace CDN URLs, parse booleans
   */
  const processContext = useCallback((context) => {
    if (!context) return null;

    try {
      // Replace all CDN URLs
      const processedContext = replaceCDNUrl(context, CDN_OLD_URL, CDN_NEW_URL);

      // Parse boolean strings in top level
      Object.keys(processedContext).forEach((key) => {
        if (typeof processedContext[key] === "string") {
          processedContext[key] = parseBooleanString(processedContext[key]);
        }
      });

      return processedContext;
    } catch (error) {
      console.error("Error processing context:", error);
      return context;
    }
  }, []);

  /**
   * Setup popup timer if configured
   */
  const setupPopupTimer = useCallback((context) => {
    if (
      context &&
      (window?.context?.enter_popup || context?.enter_popup) &&
      ![null, undefined].includes(context.duration_show_popup)
    ) {
      const timerId = setTimeout(() => {
        setPopup(true);
      }, context.duration_show_popup * 1000);

      return () => clearTimeout(timerId); // Cleanup function
    }
  }, []);

  useEffect(() => {
    try {
      const context = parseWindowContext();
      const processedContext = processContext(context);

      if (processedContext) {
        setWindowContext(processedContext);

        // Determine media type for background
        if (processedContext.background_url) {
          const mediaType = detectMediaType(processedContext.background_url);
          setIsImage(mediaType === "image");
          setIsVideo(mediaType === "video");
          setBackgroundUrl(processedContext.background_url);
        }

        // Setup popup if needed
        setupPopupTimer(processedContext);
      }
    } catch (error) {
      console.error("Error in App initialization:", error);
    } finally {
      setMounted(true);
    }
  }, [parseWindowContext, processContext, setupPopupTimer]);

  /**
   * Close modal popup
   */
  const closePopup = useCallback(() => {
    setPopup(false);
  }, []);

  return (
    <>
      {mounted && (
        <>
          {popup && (
            <div id="modalpopup" className="modal">
              <div className="modal-content">
                <span className="close" onClick={closePopup}>
                  &times;
                </span>
                <p>this is popup</p>
              </div>
            </div>
          )}

          <div
            style={{
              backgroundImage: `url(${windowContext && windowContext.selected_template !== "template_4" ? backgroundUrl : ""})`,
              backgroundColor: `${windowContext && windowContext.selected_template !== "template_4" ? "#fff" : "#000"}`,
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
              width: "100%",
              height: "100vh",
              backgroundSize: "cover",
            }}
            className={windowContext?.selected_template}
          >
            <Routes>
              <Route
                key="dynamic"
                path="*"
                element={<DynamicComponent value={windowContext} />}
              ></Route>
            </Routes>
          </div>
        </>
      )}
    </>
  );
}
